import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid2,
  CircularProgress,
} from "@mui/material";
import styles from "./FileTable.module.scss";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import { ReactComponent as Download } from "../../../../../../../GlobalAssets/svg/Download.svg";
import TooltipType from "../../../../../../../GlobalComponents/TooltipComponent/TooltipComponent";
import { CP } from "../../../../../../../Utils/roconConstants";
import { useSelector } from "react-redux";

const FileTable = (props) => {
  const {
    upload,
    rows,
    uploadClick,
    handleHomeClick,
    openPath,
    pathAttributes,
    setItem,
    sites,
  } = props;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  let userDetails = useSelector((state) => state.users.userDetails?.theme);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const DownloadRestoreButton = ({ title, IconComponent, onClick }) => (
    <Grid2 className={styles.iconBox} onClick={onClick}>
      <TooltipType title={title} arrow placement="top">
        <IconComponent />
      </TooltipType>
    </Grid2>
  );

  const renderPath = () =>
    pathAttributes?.length > 0 ? (
      Array.isArray(pathAttributes) &&
      pathAttributes?.map((item, index) => (
        <Grid2
          onClick={() => openPath(index)}
          key={index}
          className={styles.pathItem}
        >
          <Grid2 className={styles.color}>/</Grid2>
          <Grid2 className={styles.color} onClick={setItem}>
            {item}
          </Grid2>
        </Grid2>
      ))
    ) : (
      <Grid2 className={styles.color}>/</Grid2>
    );

  // `sites.files` only exists once the getFile API has returned, so we can tell
  // "still loading" (undefined) apart from "loaded but this folder is empty".
  const hasLoaded = !!sites?.files;
  const hasContent = rows?.length > 0;

  return (
    <Grid2>
      <TableContainer className={styles.main}>
        <Grid2 className={styles.display}>
          <Grid2 className={styles.icon}>
            <HomeOutlinedIcon
              onClick={handleHomeClick}
              className={styles.homeIcon}
            />
            <Grid2 className={styles.path}> {renderPath()}</Grid2>
          </Grid2>

          <Grid2 className={styles.header} onClick={uploadClick}>
            {upload}
          </Grid2>
        </Grid2>
        {hasContent ? (
          <>
            <Table>
              <TableHead className={styles.tableHead}>
                <TableRow className={styles.tablerow}>
                  <TableCell className={styles.tableHeaderText}>Name</TableCell>
                  <TableCell className={styles.tableHeaderText}>Size</TableCell>
                  <TableCell className={styles.tableHeaderText}>
                    Created
                  </TableCell>
                  <TableCell className={styles.tableHeaderText}>
                    Download
                  </TableCell>
                  <TableCell className={styles.tableHeaderText}>
                    Delete
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((item, index) => (
                  <TableRow key={index} className={styles.row}>
                    <TableCell className={styles.tableBodyText}>
                      {item.name}
                    </TableCell>
                    <TableCell className={styles.tableBodyText}>
                      {item.size}
                    </TableCell>
                    <TableCell className={styles.tableBodyText}>
                      {item.created}
                    </TableCell>
                    <TableCell className={styles.download}>
                      <DownloadRestoreButton
                        title="Download"
                        IconComponent={Download}
                        onClick={() => item.download && item.download()}
                      />
                    </TableCell>
                    <TableCell className={styles.delete} onClick={item.delete}>
                      <DeleteOutlinedIcon className={styles.deleteIcon} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              sx={{
                fontFamily: "'Nunito', sans-serif",
                "& .MuiTablePagination-selectLabel": {
                  color: userDetails === "dark" ? "#ffffff" : "#000000",
                  fontFamily: "'Nunito', sans-serif",
                },
                "& .MuiTablePagination-displayedRows": {
                  color: userDetails === "dark" ? "#ffffff" : "#000000",
                  fontFamily: "'Nunito', sans-serif",
                },
                "& .MuiTablePagination-actions": {
                  color: userDetails === "dark" ? "#ffffff" : "#000000",
                  fontFamily: "'Nunito', sans-serif",
                },
                "& .MuiTablePagination-input": {
                  color: userDetails === "dark" ? "#ffffff" : "#000000",
                  fontFamily: "'Nunito', sans-serif",
                },
                "& .MuiSelect-icon": {
                  color: userDetails === "dark" ? "#ffffff" : "#000000",
                  fontFamily: "'Nunito', sans-serif",
                },
                "& .MuiButtonBase-root.Mui-disabled": {
                  color: userDetails === "dark" ? "#ffffff" : "#000000",
                  opacity: "0.5",
                  fontFamily: "'Nunito', sans-serif",
                },
                "& .MuiTablePagination-select": {
                  fontFamily: "'Nunito', sans-serif",
                },
              }}
              component="div"
              count={rows?.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 15]}
            />
          </>
        ) : hasLoaded ? (
          <Grid2 className={styles.empty}>
            <FolderOffOutlinedIcon className={styles.emptyIcon} />
            <Grid2 className={styles.emptyTitle}>This folder is empty</Grid2>
            <Grid2 className={styles.emptySub}>
              Upload files to get started.
            </Grid2>
            <button
              type="button"
              className={styles.emptyBtn}
              onClick={uploadClick}
            >
              <CloudUploadOutlinedIcon fontSize="small" />
              {upload}
            </button>
          </Grid2>
        ) : (
          <Grid2 className={styles.loading}>
            <CircularProgress size={CP.SIZE} className={styles.loadingColor} />
          </Grid2>
        )}
      </TableContainer>
    </Grid2>
  );
};

export default FileTable;
